'use client'

import { ArrowRight } from 'lucide-react'
import Image from 'next/image'
import { useEffect, useMemo, useRef, useState } from 'react'
import { trackEvent } from '@/components/@analitycs/trackEvent'
import { ViewContentEvent } from '@/components/@analitycs/view-content-event'
import SubscriptionButton from '@/components/features/cursos/subscription-button'
import PurchaseButtonContent from '@/components/template-one/cursos/track/purchase-button-content'
import PurchaseButtonHero from '@/components/template-one/cursos/track/purchase-button-hero'
import { priceFormat } from '@/helpers/price-format'
import type { GetProductOutput } from '@/http/zamply/products/get-product-schema'
import clipboardIcon from '@/public/icons/clipboard.svg'
import abedSvg from '@/public/img/abed-logo.svg'
import Comment1 from '@/public/img/comentario-1.webp'
import Comment2 from '@/public/img/comentario-2.webp'
import Comment3 from '@/public/img/comentario-3.webp'
import Comment4 from '@/public/img/comentario-4.webp'
import Comment5 from '@/public/img/comentario-5.webp'
import Comment6 from '@/public/img/comentario-6.webp'
import eMecImg from '@/public/img/e-mec.png'
import governoSvg from '@/public/img/governo-logo.svg'
import imgTrilha from '@/public/img/img-trilha.webp'
import trilha1Img from '@/public/img/img-trilha-1.webp'
import trilha2Img from '@/public/img/img-trilha-2.webp'
import trilha3Img from '@/public/img/img-trilha-3.webp'
import inciBrasilSvg from '@/public/img/inci-brasil-logo.svg'
import membrosImg from '@/public/img/membros.webp'
import seloInci from '@/public/img/selo-inci.webp'
import wavyArrowImg from '@/public/img/seta-ondulada.svg'
import { getDeviceInfo } from '@/utils/get-device-info'
import { sessionCookie } from '@/utils/session-cookie'

interface TrackTemplateProps {
  track: GetProductOutput
}

export default function TrackTemplate({ track }: TrackTemplateProps) {
  const hasTrackedRef = useRef(false)
  const [referrer, setReferrer] = useState('')
  const session = sessionCookie

  const status = session?.state.status
  const fullName = session?.state.profile.full_name
  const email = session?.state.profile.email
  const userId = session?.state.profile.reference
  const externalId = session?.state.profile.external_id
  const device = useMemo(() => getDeviceInfo(), [])

  useEffect(() => {
    setReferrer(document.referrer)
  }, [])

  useEffect(() => {
    if (hasTrackedRef.current || !referrer) return

    const eventDataSelectItem = {
      event: 'vitrine',
      eventName: 'select_item',
      customDataObject: {
        environment: 'Vitrine',
        item_course: track?.reference ?? track.external_id,
        item_referrer: referrer,
        item_device: device,
        ...(status === 'authenticated' && {
          user_id: userId ?? externalId,
          item_name: fullName,
          item_email: email,
        }),
      },
    }

    const eventDataViewItem = {
      event: 'vitrine',
      eventName: 'view_item',
      customDataObject: {
        environment: 'Vitrine',
        item_course: track?.reference ?? track.external_id,
        item_referrer: referrer,
        item_device: device,
        ...(status === 'authenticated' && {
          user_id: userId ?? externalId,
          item_name: fullName,
          item_email: email,
        }),
      },
    }

    trackEvent(eventDataSelectItem)
    trackEvent(eventDataViewItem)

    hasTrackedRef.current = true
  }, [status, fullName, email, device, referrer, userId, track, externalId])

  const TRACK_DISCOUNT_PERCENTAGE = 60
  const valueToConvert = Number(track.amount)
  const originalPrice =
    valueToConvert / (1 - TRACK_DISCOUNT_PERCENTAGE / 100)
  const installmentValue = valueToConvert / 10

  return (
    <>
      <ViewContentEvent
        id={track?.reference ?? track.external_id}
        name={track.name}
        price={valueToConvert}
      />
      <main className="bg-gray-50 font-rethink">
        <section className="relative overflow-hidden bg-[#3A0077] pt-16 pb-17">
          <div className="container flex flex-wrap items-center justify-between">
            <div className="z-10 w-full max-w-135.75">
              <div>
                <h1 className="font-bold text-3.5xl text-[#EFE2F9] leading-10 lg:text-4.5xl lg:leading-11">
                  {track?.name || ''}
                </h1>
                <p className="mt-2 font-medium text-[#DFB6FF] text-sm lg:text-lg lg:leading-6">
                  {track.description || ''}
                </p>
              </div>

              <PurchaseButtonHero track={track} />

              <div className="relative mt-6 max-w-73.5 rounded-1xl border border-[#A64EFF] p-6 leading-none lg:max-w-99.5 lg:py-4">
                <span className="font-medium text-[#DFB6FF] text-lg lg:text-2xl">
                  {track.collection?.products?.length} Certificados
                </span>
                <strong className="block text-[#EFE2F9] text-xl lg:mt-2 lg:text-3.5xl">
                  Totalizando {track.collection?.total_workload} Horas
                </strong>
                <Image
                  alt="Selo da INCI Brasil"
                  className="-right-8 absolute top-4"
                  height={65}
                  src={seloInci}
                  width={65}
                />
              </div>
            </div>

            <div className="z-10 mt-10 rounded-1xl bg-[#9500A1] p-5 lg:mt-0">
              <Image
                alt=""
                className="rounded-1xl"
                height={387}
                src={track?.image_url ?? ''}
                width={434}
              />
            </div>
          </div>

          <div
            className="-top-20 -right-80 absolute h-209.5 w-206.25 rounded-[52.375rem] opacity-30 blur-[50px]"
            style={{
              background:
                'linear-gradient(181deg, #E913FC 3.81%, #760081 99.28%)',
            }}
          />

          <div
            className="-top-20 -left-80 absolute h-175 w-166.5 rounded-[52.375rem] opacity-10 blur-[50px]"
            style={{
              background:
                'linear-gradient(181deg, #E913FC 3.81%, #760081 99.28%)',
            }}
          />
        </section>

        <section className="-mt-6 rounded-t-1xl bg-gray-50 pt-10">
          <div className="container">
            <div className="flex flex-col items-center justify-between lg:flex-row">
              <div className="items-center] flex flex-row items-center gap-x-2 gap-y-4 lg:flex-col">
                <Image
                  alt="Membros"
                  height={40}
                  src={membrosImg}
                  width={119.54}
                />

                <div className="max-w-40">
                  <p className="text-[#59009E] text-lg leading-5">
                    Junte-se a <strong>+590 membros</strong>
                  </p>
                </div>
              </div>

              <div className="mt-6 flex w-full flex-wrap items-center justify-center gap-x-16 gap-y-12 text-center font-medium text-primary-400 text-xs lg:mt-0 lg:justify-end">
                <div className="flex flex-col items-center gap-4 md:flex-row md:text-start">
                  <span>
                    Somos <br /> Associados
                  </span>
                  <Image
                    alt="Logo da ABED"
                    height={40}
                    loading="lazy"
                    src={abedSvg}
                    width={117.037}
                  />
                </div>

                <div className="flex flex-col items-center gap-4 md:flex-row md:text-start">
                  <span>
                    Seguimos as <br /> Leis do País
                  </span>
                  <Image
                    alt="Logo do Governo Federal"
                    height={40}
                    loading="lazy"
                    src={governoSvg}
                    width={117.037}
                  />
                </div>

                <div className="flex flex-col items-center gap-4 md:flex-row md:text-start">
                  <span>
                    Entidade <br /> Mantenedora
                  </span>
                  <Image
                    alt="Logo da INCI Brasil"
                    height={40}
                    loading="lazy"
                    src={inciBrasilSvg}
                    width={117.037}
                  />
                </div>

                <div className="flex flex-col items-center gap-4 md:flex-row md:text-start">
                  <span>
                    Credenciado <br /> ao MEC
                  </span>
                  <Image
                    alt="Logo da INCI Brasil"
                    height={40}
                    loading="lazy"
                    src={eMecImg}
                    width={117.037}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-50 pt-16">
          <div className="m-auto max-w-60 lg:max-w-full">
            <h2 className="text-center font-bold text-[#430283] text-xl">
              O que os nossos alunos comentam sobre a trilha{' '}
            </h2>
          </div>
          <div className="mx-auto mt-4 flex max-w-280.25 flex-wrap justify-center gap-x-6 gap-y-4 lg:mt-6 lg:text-2xl">
            <div className="flex flex-col gap-y-4">
              <Image alt="" src={Comment5} />
              <Image alt="" src={Comment2} />
            </div>
            <div className="flex flex-col gap-y-4">
              <Image alt="" src={Comment4} />
              <Image alt="" src={Comment1} />
            </div>
            <div className="flex flex-col gap-y-4">
              <Image alt="" src={Comment3} />
              <Image alt="" src={Comment6} />
            </div>
          </div>
        </section>

        <section className="pt-12">
          <div className="container">
            <div className="mx-auto w-full max-w-2xl text-center">
              <h2 className="font-bold text-3.5xl text-[#430283] leading-9 lg:text-4.5xl lg:leading-none">
                Inspire a próxima geração de maneira impactante e significativa.
              </h2>
              <p className="mt-2 font-normal text-[#40364A] text-sm">
                Fortaleça suas habilidades pedagógicas, promova inovação em sala
                de aula e proporcione um ambiente de aprendizado inspirador para
                seus alunos.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-2">
            <Image alt="" height={273} src={trilha1Img} width={373} />
            <Image alt="" height={357} src={trilha2Img} width={494} />
            <Image alt="" height={273} src={trilha3Img} width={373} />
          </div>

          <div className="-mb-12 container mt-17">
            <div className="flex items-end gap-2">
              <h3 className="font-medium text-[#8747C7] text-base lg:text-2xl">
                O que você vai aprender:
              </h3>
              <div className="h-0.5 flex-1 bg-[#E913FCFC]" />
            </div>

            <div className="mt-6 flex w-full flex-wrap items-center justify-between gap-y-2">
              {track.collection?.products?.map(product => (
                <div
                  className="flex h-79 w-full max-w-88 flex-col items-center gap-y-3 rounded-1xl border border-[#CEA6F5] bg-[#F7F0F7] px-6 py-4"
                  key={product.name}
                >
                  <Image
                    alt={product?.name}
                    className="w-full rounded lg:w-max"
                    height={200}
                    key={product?.name}
                    src={product.image_url ?? ''}
                    width={304}
                  />
                  <span className="font-bold">{product.name}</span>
                </div>
              ))}
            </div>

            <div className="mx-auto mt-6 flex w-full max-w-[24rem] items-center gap-x-2 lg:mt-10">
              <Image alt="" height={32} src={wavyArrowImg} width={66.462} />
              <PurchaseButtonContent track={track} />
              <Image
                alt=""
                className="-scale-x-100"
                height={32}
                src={wavyArrowImg}
                width={66.462}
              />
            </div>

            <div className="mt-21 flex flex-wrap justify-between gap-x-8 gap-y-6 lg:flex-nowrap">
              <div className="h-72 flex-1 rounded-xl border border-[#D7AADB] bg-gray-50 p-8">
                <div>
                  <h1 className="font-medium text-[#430283] text-lg leading-6">
                    Torne-se um educador confiante e certificado.
                  </h1>
                  <div
                    className="mt-1 h-0.5 w-23"
                    style={{
                      background:
                        'linear-gradient(90deg, rgba(233, 19, 252, 0.99) 0%, #8713FC 49.56%, rgba(191, 19, 252, 0.99) 102.32%), #D9D9D9',
                    }}
                  />
                </div>

                <p className="mt-4 font-medium text-[#ABACAD] text-base leading-5">
                  Avance na trilha de aprendizado e obtenha certificados
                  reconhecidos nacionalmente. Ofereça ensino e suporte de alta
                  qualidade aos alunos. Seja um educador certificado em cuidados
                  infantis, inovador e confiante.
                </p>
              </div>

              <div className="h-72 flex-1 rounded-xl border border-[#D7AADB] bg-gray-50 p-8">
                <div>
                  <h1 className="font-medium text-[#430283] text-lg leading-6">
                    Alcance maiores resultados com seus alunos
                  </h1>
                  <div
                    className="mt-1 h-0.5 w-23"
                    style={{
                      background:
                        'linear-gradient(90deg, rgba(233, 19, 252, 0.99) 0%, #8713FC 49.56%, rgba(191, 19, 252, 0.99) 102.32%), #D9D9D9',
                    }}
                  />
                </div>

                <p className="mt-4 font-medium text-[#ABACAD] text-base leading-5">
                  Acesse videoaulas, apostilas digitais e materiais
                  complementares. Aprimore suas habilidades e esteja pronto para
                  oferecer educação de qualidade e estimular o desenvolvimento
                  dos pequenos.
                </p>
              </div>

              <div className="h-72 flex-1 rounded-xl border border-[#D7AADB] bg-gray-50 p-8">
                <div>
                  <h1 className="font-medium text-[#430283] text-lg leading-6">
                    Impulsione sua carreira com certificações reconhecidas.
                  </h1>
                  <div
                    className="mt-1 h-0.5 w-23"
                    style={{
                      background:
                        'linear-gradient(90deg, rgba(233, 19, 252, 0.99) 0%, #8713FC 49.56%, rgba(191, 19, 252, 0.99) 102.32%), #D9D9D9',
                    }}
                  />
                </div>

                <p className="mt-4 font-medium text-[#ABACAD] text-base leading-5">
                  Adquira habilidades valiosas na educação infantil, com
                  certificados de conclusão de 40 a 360 horas por fase. Comprove
                  sua experiência e avance em concursos e progressão de
                  carreira. Invista em sua carreira e seja um cuidador infantil
                  qualificado e confiante.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#3A0077] pt-28 pb-8 lg:pb-52">
          <div className="container">
            <div className="flex flex-wrap justify-between gap-y-10 lg:flex-nowrap">
              <h1 className="font-bold text-2.5xl text-[#F5ECFE] leading-8">
                Veja um pouco de cada curso que compõe a trilha:
              </h1>
              <div className="relative w-full space-y-10 lg:space-y-2">
                <div
                  className="absolute top-14 left-4.5 h-full w-px bg-red-600 lg:w-0.5"
                  style={{
                    background:
                      'linear-gradient(180deg, #5300A7 0%, rgba(118, 19, 217, 0.15) 86.82%)',
                  }}
                />
                {track.collection?.products?.map((course, i) => (
                  <div className="flex items-start gap-x-4" key={course.name}>
                    <div className="z-10 flex min-h-10 min-w-10 items-center justify-center rounded-full bg-[#8B4EC9] text-white">
                      {i + 1}
                    </div>
                    <div className="flex flex-col lg:flex-row">
                      <div className="w-full rounded bg-[#56139A]">
                        <div className="px-4 pt-6 pb-6">
                          <div className="flex items-start gap-2.5">
                            <span className="">
                              <ArrowRight className="size-6 rounded bg-[#A64EFF] p-1 text-[#FCFAFF]" />
                            </span>
                            <h3 className="font-bold text-[#FBFBFB] text-base">
                              {course.name}
                            </h3>
                          </div>

                          <p className="mt-2.5 font-medium text-[#D8B3FC] text-sm">
                            {course.description}
                          </p>
                        </div>
                      </div>
                      <div className="rounded bg-[#A514FC] py-4 lg:min-w-21">
                        <span className="flex h-full items-center justify-center gap-2 font-bold text-[#FBFBFB] text-lg">
                          <Image
                            alt=""
                            height={24}
                            src={clipboardIcon}
                            width={24}
                          />
                          {course.max_workload}h
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="mt-14 h-0.5 w-full"
              style={{
                background:
                  'linear-gradient(270deg, rgba(233, 19, 252, 0.00) 18.62%, rgba(233, 19, 252, 0.99) 49.48%, rgba(233, 19, 252, 0.00) 82.99%)',
              }}
            />

            <div
              className="flex flex-wrap items-center justify-center gap-x-32 gap-y-7 pt-14"
              id="buy"
            >
              <div className="w-full max-w-88">
                <p className="font-normal text-[#F3DAFE] text-base leading-5 lg:text-lg lg:leading-6">
                  Equipe-se com conhecimentos atualizados e habilidades
                  práticas, com a nossa trilha de aprendizado personalizada
                </p>
                <div className="mt-4">
                  <h2 className="font-medium text-4.5xl text-[#E913FC] leading-11">
                    Acesse todos os {track.collection?.products?.length}{' '}
                    Certificados -{' '}
                  </h2>
                  <span className="font-medium text-2xl text-[#F59F23]">
                    Totalizando{' '}
                    {new Intl.NumberFormat('pt-br', {
                      style: 'decimal',
                    }).format(track.collection?.total_workload)}{' '}
                    Horas
                  </span>
                </div>

                <span className="mt-4 font-normal text-[#F3DAFE] text-base leading-4">
                  De <span className="text-sm line-through">{priceFormat(originalPrice)}</span> por
                  <strong className="block">Em 10x de</strong>
                </span>

                <div className="mt-2 leading-none">
                  <div className="relative">
                    <span className="font-bold text-[#FFF59B] text-[5rem]">
                      {priceFormat(installmentValue)}
                    </span>
                    <span className="-bottom-2.5 -rotate-6 absolute right-14 w-max rounded-full bg-white px-2 py-1 font-bold text-[#2A0252] text-xs">
                      (Desconto de {TRACK_DISCOUNT_PERCENTAGE}%)
                    </span>
                  </div>

                  <small className="font-medium text-[#D16FDA] text-sm">
                    Ou {priceFormat(valueToConvert)} à vista
                  </small>
                </div>
                <SubscriptionButton product={track} />
              </div>

              <Image
                alt="Home sorrindo"
                height={450}
                src={imgTrilha}
                width={448}
              />
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
